import type { Language, LanguageSource } from '@hero/schema';
import { isLanguageNeutralInbound, stripAllowlistedEmoji } from './inbound-reply-tokens';
import { looksLikeRomanUrdu } from './roman-urdu';
import { distinctiveCharsRegex, matchExplicitSwitch, matchGreeting } from './packs/registry';
import { urPack } from './packs/ur';

/**
 * How much one inbound message says about the language the person wants
 * to be answered in, weakest first.
 *
 * A thread used to flip on any Latin letters: «Majid ali» (a name) and
 * «Riyadh to qaseem» (a lane) turned an Arabic thread English, and the
 * reply came back half English, half Arabic (LangSmith thread
 * 6abbe0f5…, 29 Sep 2026). Names, lanes, numbers and plates now carry
 * no evidence at all, and English needs a real sentence.
 */
export type LanguageEvidenceStrength =
  | 'none'
  | 'hint'
  | 'typed_sentence'
  | 'voice_confident'
  | 'explicit_choice';

export interface LanguageEvidence {
  language: Language | null;
  strength: LanguageEvidenceStrength;
}

export const NO_LANGUAGE_EVIDENCE: LanguageEvidence = { language: null, strength: 'none' };

const URDU_CHARS_RE = distinctiveCharsRegex(urPack) as RegExp;
const ARABIC_SCRIPT_RE = /[\u0600-\u06FF]/;
const LATIN_LETTERS_RE = /[A-Za-z]/;
const URL_RE = /(?:https?:\/\/|www\.)\S+|\b[\w-]+(?:\.[\w-]+)+\/\S*/gi;
/** Phones, plates, weights, dates — digits in any script plus their punctuation. */
const NUMERIC_TOKEN_RE = /[+]?[\d٠-٩۰-۹][\d٠-٩۰-۹\s./:()-]*/g;
const EMOJI_RE = /(?:\p{Extended_Pictographic}|\u200d|\uFE0F)/gu;

/**
 * English words that make a Latin message a sentence rather than a
 * name or a place. A two-word Latin message with none of these
 * («Majid ali», «Al qassim») is neutral.
 */
const ENGLISH_FUNCTION_WORDS: ReadonlySet<string> = new Set([
  'i', "i'm", 'im', "i've", "i'll", 'me', 'my', 'we', 'us', 'our', 'you', 'your',
  'he', 'she', 'it', 'they', 'them', 'his', 'her', 'their', 'this', 'that',
  'these', 'those', 'there', 'here', 'the', 'a', 'an', 'is', 'are', 'am', 'was',
  'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', "don't",
  'can', 'could', 'will', 'would', 'should', 'please', 'want', 'need', 'what',
  'where', 'when', 'why', 'how', 'who', 'which', 'not', 'and', 'or', 'but',
  'with', 'for', 'of', 'in', 'on', 'at', 'hello', 'hi', 'hey', 'thanks',
  'thank', 'good', 'morning', 'send', 'give', 'get', 'show', 'add', 'assign',
  'create', 'check', 'update', 'change', 'book', 'track', 'list', 'issue',
  'help', 'let', "let's", 'just', 'also', 'again', 'now', 'today', 'tomorrow',
]);

const LANE_CONNECTORS = new Set(['to', 'from', 'till', 'until', 'toward', 'towards', '->', '→', '-', 'al', 'el']);

/** «Riyadh to qaseem», «from Riyadh to Al qassim», «jeddah - dammam». */
const looksLikeLatinLane = (words: string[]): boolean => {
  if (words.length < 3 || words.length > 7) return false;
  const lower = words.map(w => w.toLowerCase());
  if (!lower.some(w => w === 'to' || w === '->' || w === '→' || w === '-')) return false;
  return lower.every(w => LANE_CONNECTORS.has(w) || !ENGLISH_FUNCTION_WORDS.has(w));
};

/** «من الرياض الى القصيم», «الرياض لجدة». */
const looksLikeArabicLane = (text: string): boolean => {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 6) return false;
  return /(^|\s)(من\s+)?\S+(\s+\S+)?\s+(الى|إلى|ل\S+|لـ\s*\S+|->|→|-)/u.test(text);
};

/**
 * "Speak English", «تكلم عربي», «اردو میں بات کریں», or a bare language
 * name. Only short messages count — a sentence that merely mentions
 * Arabic («the waybill is in Arabic») is not a choice.
 */
export const explicitLanguageChoice = (text: string): Language | null => {
  const trimmed = text.trim().replace(/[.!؟?،,]+$/u, '').trim();
  if (!trimmed || trimmed.length > 40) return null;
  return matchExplicitSwitch(trimmed) as Language | null;
};

const substantiveText = (text: string): string =>
  stripAllowlistedEmoji(text)
    .replace(URL_RE, ' ')
    .replace(EMOJI_RE, ' ')
    .replace(NUMERIC_TOKEN_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const classifyLatin = (text: string): LanguageEvidence => {
  const words = text.split(/\s+/).filter(Boolean);
  if (looksLikeLatinLane(words)) return NO_LANGUAGE_EVIDENCE;
  if (looksLikeRomanUrdu(text)) return { language: 'ur', strength: 'typed_sentence' };
  const functionWords = words.filter(w =>
    ENGLISH_FUNCTION_WORDS.has(w.toLowerCase().replace(/[^a-z']/g, '')),
  ).length;
  if (functionWords === 0) {
    return words.length <= 3 ? NO_LANGUAGE_EVIDENCE : { language: 'en', strength: 'hint' };
  }
  return words.length >= 3
    ? { language: 'en', strength: 'typed_sentence' }
    : { language: 'en', strength: 'hint' };
};

const classifyArabicScript = (text: string): LanguageEvidence => {
  if (URDU_CHARS_RE.test(text)) return { language: 'ur', strength: 'typed_sentence' };
  if (looksLikeArabicLane(text)) return NO_LANGUAGE_EVIDENCE;
  const words = text.split(/\s+/).filter(w => ARABIC_SCRIPT_RE.test(w));
  return words.length >= 2
    ? { language: 'ar', strength: 'typed_sentence' }
    : { language: 'ar', strength: 'hint' };
};

/**
 * Evidence carried by one inbound message. `detectedVoiceLanguage` is an
 * already confidence-gated STT detection (`resolveDetectedVoiceLanguage`).
 */
export const classifyLanguageEvidence = (
  text: string,
  options: { detectedVoiceLanguage?: Language } = {},
): LanguageEvidence => {
  const trimmed = (text ?? '').trim();
  const choice = explicitLanguageChoice(trimmed);
  if (choice) return { language: choice, strength: 'explicit_choice' };
  if (options.detectedVoiceLanguage) {
    return { language: options.detectedVoiceLanguage, strength: 'voice_confident' };
  }
  if (!trimmed || isLanguageNeutralInbound(trimmed)) return NO_LANGUAGE_EVIDENCE;

  // A bare greeting in a language's own script is that language: «هلا»
  // or «الو» alone used to be a one-word hint, and the picker asked an
  // Arabic speaker which language he wanted.
  const greeting = matchGreeting(trimmed);
  if (greeting) return { language: greeting as Language, strength: 'typed_sentence' };

  const substantive = substantiveText(trimmed);
  if (!substantive) return NO_LANGUAGE_EVIDENCE;

  const hasArabic = ARABIC_SCRIPT_RE.test(substantive);
  const hasLatin = LATIN_LETTERS_RE.test(substantive);
  if (hasArabic && !hasLatin) return classifyArabicScript(substantive);
  if (hasLatin && !hasArabic) return classifyLatin(substantive);
  if (!hasArabic && !hasLatin) return NO_LANGUAGE_EVIDENCE;

  // Mixed script: «الرياض se truck chahiye» is Urdu; otherwise the
  // dominant script decides, as a hint only.
  if (URDU_CHARS_RE.test(substantive) || looksLikeRomanUrdu(substantive)) {
    return { language: 'ur', strength: 'typed_sentence' };
  }
  let arabic = 0;
  let latin = 0;
  for (const ch of substantive) {
    if (ARABIC_SCRIPT_RE.test(ch)) arabic += 1;
    else if (LATIN_LETTERS_RE.test(ch)) latin += 1;
  }
  if (arabic > latin) {
    const arabicOnly = substantive.replace(/[A-Za-z]+/g, ' ').replace(/\s+/g, ' ').trim();
    return classifyArabicScript(arabicOnly);
  }
  if (latin > arabic) {
    const latinOnly = substantive.replace(/[\u0600-\u06FF]+/g, ' ').replace(/\s+/g, ' ').trim();
    const latinEvidence = classifyLatin(latinOnly);
    return latinEvidence.language ? { language: latinEvidence.language, strength: 'hint' } : latinEvidence;
  }
  return NO_LANGUAGE_EVIDENCE;
};

/** Weakest first. A write needs the new source to rank at or above the stored one. */
export const LANGUAGE_SOURCE_RANK: Record<LanguageSource, number> = {
  default: 0,
  hint: 1,
  nationality: 2,
  accent: 3,
  typed: 4,
  voice: 5,
  user_choice: 6,
};

const SOURCE_FOR_STRENGTH: Record<Exclude<LanguageEvidenceStrength, 'none'>, LanguageSource> = {
  hint: 'hint',
  typed_sentence: 'typed',
  voice_confident: 'voice',
  explicit_choice: 'user_choice',
};

/**
 * The thread has no language anyone chose or showed — unset, or stamped
 * from a default (a first message that was only photos). The script of
 * what they type next still decides it.
 */
export const hasNoThreadLanguage = (state: { language?: Language; source?: LanguageSource }): boolean =>
  !state.language || state.source === 'default';

/** Two `hint` messages in a row in one language count as a sentence. */
export const LANGUAGE_HINT_STREAK_TO_SWITCH = 2;

export interface ThreadLanguageState {
  language?: Language;
  /** Absent with a language set = legacy row; ranks as `typed`. */
  source?: LanguageSource;
  hint?: { language: Language; count: number };
}

/** The stored source, with legacy rows (language, no source) read as `typed`. */
export const effectiveLanguageSource = (state: ThreadLanguageState): LanguageSource =>
  state.language ? (state.source ?? 'typed') : 'default';

/** True when a write from `source` may replace the thread's current language. */
export const canApplyLanguageSource = (
  state: ThreadLanguageState,
  source: LanguageSource,
): boolean => LANGUAGE_SOURCE_RANK[source] >= LANGUAGE_SOURCE_RANK[effectiveLanguageSource(state)];

/**
 * The one gate every thread-language write goes through. Pure: returns
 * the next state; the caller persists whatever differs.
 */
export const decideLanguageChange = (input: {
  current: ThreadLanguageState;
  evidence: LanguageEvidence;
}): ThreadLanguageState => {
  const { current, evidence } = input;
  if (!evidence.language || evidence.strength === 'none') return current;

  let source = SOURCE_FOR_STRENGTH[evidence.strength];
  let hint: ThreadLanguageState['hint'];
  if (evidence.strength === 'hint') {
    const count = current.hint?.language === evidence.language ? current.hint.count + 1 : 1;
    hint = { language: evidence.language, count };
    if (count >= LANGUAGE_HINT_STREAK_TO_SWITCH) source = 'typed';
  }

  const currentSource = effectiveLanguageSource(current);
  if (evidence.language === current.language) {
    const upgraded =
      LANGUAGE_SOURCE_RANK[source] > LANGUAGE_SOURCE_RANK[currentSource] ? source : currentSource;
    return { language: current.language, source: upgraded, hint: undefined };
  }
  if (!canApplyLanguageSource(current, source)) {
    return { ...current, hint };
  }
  return { language: evidence.language, source, hint: undefined };
};
